import Vapi from '@vapi-ai/web';

export interface VapiMessage {
  type: 'function-call' | 'speech-update' | 'transcript' | 'conversation-update';
  role?: 'user' | 'assistant' | 'system';
  transcriptType?: 'partial' | 'final';
  transcript?: string;
}

export interface AssistantOverrides {
  backgroundSound?: 'office' | 'off' | string;
  firstMessage?: string;
  model?: {
    provider: 'openai';
    model: string;
    messages: Array<{
      role: 'system' | 'user' | 'assistant';
      content: string;
    }>;
  };
  variableValues?: Record<string, string>;
  recordingEnabled?: boolean;
  silenceTimeoutSeconds?: number;
  maxDurationSeconds?: number;
}

export interface VapiConfig {
  assistantId: string;
  publicKey: string;
  assistantOverrides?: AssistantOverrides;
  onMessage?: (message: VapiMessage) => void;
  onCallStart?: () => void;
  onCallEnd?: () => void;
  onError?: (error: Error) => void;
  onTranscript?: (transcript: string, role: 'user' | 'assistant') => void;
}

export class VapiClient {
  private vapi: Vapi;
  private config: VapiConfig;
  private isActive = false;

  constructor(config: VapiConfig) {
    this.config = config;
    console.log('🚀 Creating Vapi instance with publicKey');
    this.vapi = new Vapi(config.publicKey);
    console.log('✅ Vapi instance created');
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.vapi.on('call-start', () => {
      console.log('✅ Vapi call started');
      this.isActive = true;
      this.config.onCallStart?.();
    });

    this.vapi.on('call-end', () => {
      console.log('🔚 Vapi call ended');
      this.isActive = false;
      this.config.onCallEnd?.();
    });

    this.vapi.on('speech-start', () => {
      console.log('🗣️ Assistant started speaking');
    });

    this.vapi.on('speech-end', () => {
      console.log('🤐 Assistant stopped speaking');
    });

    this.vapi.on('message', (message: VapiMessage) => {
      // console.log('📨 Vapi message:', message);
      this.config.onMessage?.(message);

      if (message.type === 'transcript' && message.transcript) {
        const role = message.role === 'assistant' ? 'assistant' : 'user';

        if (message.transcriptType === 'final') {
          // console.log(`📝 Final transcript (${role}):`, message.transcript);
          this.config.onTranscript?.(message.transcript, role);
        }
      }
    });

    this.vapi.on('error', (error: any) => {
      console.error('❌ Vapi error:', error);

      let errorMessage = 'Unknown Vapi error';
      let isNaturalEnd = false;

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        const errorType = error.error?.type || error.type;
        const errorMsg = error.errorMsg || error.error?.msg || error.message || error.error;

        if (errorType === 'ejected' || errorMsg === 'Meeting has ended') {
          isNaturalEnd = true;
          console.log('✅ Call ended naturally (interviewer finished)');
        }

        errorMessage = errorMsg || JSON.stringify(error);
      }

      if (!isNaturalEnd) {
        const err = new Error(errorMessage);
        this.config.onError?.(err);
      }
    });
  }

  async start(): Promise<void> {
    try {
      console.log('🚀 Starting Vapi call with assistant:', this.config.assistantId);

      if (this.config.assistantOverrides) {
        console.log('📝 Using assistant overrides:', JSON.stringify(this.config.assistantOverrides, null, 2));

        await this.vapi.start(this.config.assistantId, this.config.assistantOverrides as any);
      } else {
        console.log('📝 No assistant overrides - using default assistant config');
        await this.vapi.start(this.config.assistantId);
      }

      console.log('✅ Vapi call started successfully');
    } catch (error: any) {
      console.error('❌ Failed to start Vapi call:', error);
      console.error('❌ Error type:', typeof error);
      console.error('❌ Error keys:', error ? Object.keys(error) : 'null');
      console.error('❌ Error message:', error?.message);
      console.error('❌ Error stack:', error?.stack);

      let errorMessage = 'Failed to start Vapi call';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        errorMessage = error.message || error.error || error.statusText || JSON.stringify(error);
      }

      throw new Error(errorMessage);
    }
  }

  private validateOverrides(overrides: AssistantOverrides): void {
    // Check if model is provided and properly formatted
    if (overrides.model) {
      console.log('THE MODEL',overrides.model.provider);
      if (!overrides.model.provider) {
        throw new Error('assistantOverrides.model.provider is required');
      }
      if (!overrides.model.model) {
        throw new Error('assistantOverrides.model.model is required');
      }
      if (!overrides.model.messages || !Array.isArray(overrides.model.messages)) {
        throw new Error('assistantOverrides.model.messages must be an array');
      }
      
      console.log('✅ Model overrides validated:', {
        provider: overrides.model.provider,
        model: overrides.model.model,
        messageCount: overrides.model.messages.length
      });
    }
    
    if (overrides.firstMessage) {
      console.log('✅ First message override:', overrides.firstMessage.substring(0, 50) + '...');
    }
  }

  stop(): void {
    console.log('🛑 Stopping Vapi call...');
    try {
      this.isActive = false;
      this.vapi.stop();
    } catch (error) {
      console.error('Error stopping Vapi:', error);
    }
  }

  destroy(): void {
    console.log('🗑️ Destroying Vapi instance...');
    try {
      if (this.isActive) {
        this.stop();
      }

      // Remove all event listeners
      if (this.vapi.removeAllListeners) {
        this.vapi.removeAllListeners();
      }

      // Explicitly clean up any Daily resources if available
      if ((this.vapi as any).daily) {
        try {
          (this.vapi as any).daily.destroy();
        } catch (e) {
          console.log('Daily cleanup handled internally');
        }
      }

      this.isActive = false;
      console.log('✅ Vapi instance destroyed');
    } catch (error) {
      console.error('Error destroying Vapi:', error);
    }
  }

  setMuted(muted: boolean): void {
    this.vapi.setMuted(muted);
    console.log(muted ? '🔇 Microphone muted' : '🔊 Microphone unmuted');
  }

  send(message: string): void {
    if (this.isActive) {
      this.vapi.send({
        type: 'add-message',
        message: {
          role: 'system',
          content: message,
        },
      });
    }
  }

  isCallActive(): boolean {
    return this.isActive;
  }

  isMuted(): boolean {
    return this.vapi.isMuted();
  }
}